// components/Dashboard/tabs/LiveStudioTab.js
import React, { useState, useEffect } from 'react';
import Masonry from 'react-masonry-css';
import './LiveStudioTab.css';
import { toast } from 'react-hot-toast';
import LiveStudioModal from '../../Modals/LiveStudioModal/LiveStudioModal';
import BannerModal from '../../Modals/BannerModal/BannerModal';
import { API_BASE_URL_EXPORT } from '../../../config';

const POSITION_LABELS = {
  top_1: 'Üst Sol',
  top_2: 'Üst Sağ',
  bottom_1: 'Alt Sol',
  bottom_2: 'Alt Sağ',
  sidebar: 'Sidebar',
  background: 'Arka Plan'
};

const LiveStudioTab = ({ API_URL }) => {
  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState('streams');

  // Streams state
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudio, setEditingStudio] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // Banners state
  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(false);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  const breakpointColumns = {
    default: 3,
    1400: 2,
    768: 1
  };

  useEffect(() => {
    fetchStudios();
    fetchBanners();
  }, []);

  // ==================== STREAMS ====================
  const fetchStudios = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/live-studio`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setStudios(data.studios || []);
      }
    } catch (error) {
      console.error('Hata:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingStudio(null);
    setIsModalOpen(true);
  };

  const handleEdit = (studio) => {
    setEditingStudio(studio);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu içeriği silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/live-studio/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('İçerik silindi!');
        fetchStudios();
      } else {
        toast.error(data.error || 'Hata oluştu');
      }
    } catch (error) {
      console.error('Hata:', error);
      toast.error('Silme başarısız');
    }
  };

  const handleModalSubmit = async () => {
    await fetchStudios();
    setIsModalOpen(false);
    setEditingStudio(null);
  };

  // ==================== BANNERS ====================
  const fetchBanners = async () => {
    setBannersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/banners`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setBanners(data.banners || []);
      }
    } catch (error) {
      console.error('Banner yükleme hatası:', error);
      toast.error('Bannerlar yüklenemedi');
    } finally {
      setBannersLoading(false);
    }
  };

  const handleAddBanner = () => {
    setEditingBanner(null);
    setIsBannerModalOpen(true);
  };

  const handleEditBanner = (banner) => {
    setEditingBanner(banner);
    setIsBannerModalOpen(true);
  };

  const handleDeleteBanner = async (id) => {
    if (!window.confirm('Bu banner\'ı silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/banners/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Banner silindi!');
        fetchBanners();
      } else {
        toast.error(data.error || 'Hata oluştu');
      }
    } catch (error) {
      console.error('Hata:', error);
      toast.error('Silme başarısız');
    }
  };

  const handleToggleBanner = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/banners/${id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchBanners();
      } else {
        toast.error(data.error || 'Hata oluştu');
      }
    } catch (error) {
      console.error('Hata:', error);
      toast.error('İşlem başarısız');
    }
  };

  const handleBannerModalSubmit = async () => {
    await fetchBanners();
    setIsBannerModalOpen(false);
    setEditingBanner(null);
  };

  // ==================== PAGINATION ====================
  const totalPages = Math.ceil(studios.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStudios = studios.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Bilinmiyor';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ==================== RENDER ====================
  return (
    <div className="live-studio-container">
      {/* Sub-tab navigation */}
      <div className="sub-tab-nav">
        <button
          className={`sub-tab-btn ${activeSubTab === 'streams' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('streams')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
          Yayınlar
          <span className="sub-tab-count">{studios.length}</span>
        </button>
        <button
          className={`sub-tab-btn ${activeSubTab === 'banners' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('banners')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          Bannerlar
          <span className="sub-tab-count">{banners.length}</span>
        </button>
      </div>

      {/* ==================== STREAMS TAB ==================== */}
      {activeSubTab === 'streams' && (
        <>
          <div className="automation-header">
            <button onClick={handleAddNew} className="btn-primary">
              Yeni Ekle
            </button>
          </div>

          <div className="content-stats">
            <div className="stats-left">
              <span className="stats-item">
                Toplam: <strong>{studios.length}</strong> içerik
              </span>
              <span className="stats-item">
                Sayfa: <strong>{currentPage}</strong> / {totalPages || 1}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="loading-circular">
              <div className="circular-loader">
                <svg viewBox="0 0 100 100">
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#b87333" />
                      <stop offset="100%" stopColor="#d4956a" />
                    </linearGradient>
                  </defs>
                  <circle className="bg" cx="50" cy="50" r="45" />
                  <circle className="progress" cx="50" cy="50" r="45" />
                </svg>
              </div>
              <p>Yükleniyor...</p>
            </div>
          ) : studios.length === 0 ? (
            <div className="no-content">
              <p>Henüz canlı stüdyo içeriği eklenmemiş.</p>
            </div>
          ) : (
            <>
              <Masonry breakpointCols={breakpointColumns} className="masonry-grid" columnClassName="masonry-grid-column">
                {currentStudios.map((studio) => (
                  <div key={studio.id} className="live-studio-card">
                    {studio.cover_image && (
                      <div className="studio-cover">
                        <img src={`${API_BASE_URL_EXPORT}/uploads/${studio.cover_image}`} alt={studio.title} onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                    <div className="card-body">
                      <div className="content-header">
                        <h4>{studio.title}</h4>
                        <button onClick={() => handleEdit(studio)} className="icon-btn edit-icon" title="Düzenle">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </div>
                      {studio.description && (<p className="studio-description">{studio.description}</p>)}
                      <div className="studio-links">
                        <a href={studio.stream_link} target="_blank" rel="noopener noreferrer" className="studio-link primary">
                          <span>Yayın Linki</span>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M7 17L17 7M17 7H7M17 7V17" /></svg>
                        </a>
                        {studio.alternative_link && (
                          <a href={studio.alternative_link} target="_blank" rel="noopener noreferrer" className="studio-link secondary">
                            <span>Alternatif Link</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M7 17L17 7M17 7H7M17 7V17" /></svg>
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="card-footer-info">
                      <div className="footer-left">
                        <span className="footer-username">{studio.created_by_username || 'Bilinmiyor'}</span>
                        <span className="footer-date">{formatDate(studio.created_at)}</span>
                      </div>
                      <button onClick={() => handleDelete(studio.id)} className="delete-btn" title="Sil">&#128465;</button>
                    </div>
                  </div>
                ))}
              </Masonry>
              {totalPages > 1 && (
                <div className="pagination-container">
                  <button className="pagination-btn pagination-prev" onClick={goToPreviousPage} disabled={currentPage === 1}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                    Önceki
                  </button>
                  <div className="pagination-numbers">
                    {getPageNumbers().map((number, index) => (number === '...' ? (<span key={`dots-${index}`} className="pagination-dots">...</span>) : (<button key={number} className={`pagination-number ${currentPage === number ? 'active' : ''}`} onClick={() => goToPage(number)}>{number}</button>)))}
                  </div>
                  <button className="pagination-btn pagination-next" onClick={goToNextPage} disabled={currentPage === totalPages}>
                    Sonraki
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ==================== BANNERS TAB ==================== */}
      {activeSubTab === 'banners' && (
        <>
          <div className="automation-header">
            <button onClick={handleAddBanner} className="btn-primary">
              Yeni Banner Ekle
            </button>
          </div>

          <div className="content-stats">
            <div className="stats-left">
              <span className="stats-item">
                Toplam: <strong>{banners.length}</strong> banner
              </span>
              <span className="stats-item">
                Aktif: <strong>{banners.filter(b => b.is_active === 1).length}</strong>
              </span>
            </div>
          </div>

          {bannersLoading ? (
            <div className="loading-circular">
              <div className="circular-loader">
                <svg viewBox="0 0 100 100">
                  <defs>
                    <linearGradient id="gradient-banner" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#b87333" />
                      <stop offset="100%" stopColor="#d4956a" />
                    </linearGradient>
                  </defs>
                  <circle className="bg" cx="50" cy="50" r="45" />
                  <circle className="progress" cx="50" cy="50" r="45" />
                </svg>
              </div>
              <p>Yükleniyor...</p>
            </div>
          ) : banners.length === 0 ? (
            <div className="no-content">
              <p>Henüz banner eklenmemiş.</p>
            </div>
          ) : (
            <div className="banner-grid">
              {banners.map((banner) => (
                <div key={banner.id} className={`banner-card ${banner.is_active === 0 ? 'banner-inactive' : ''}`}>
                  <div className="banner-preview">
                    <img
                      src={`${API_BASE_URL_EXPORT}/uploads/${banner.image}`}
                      alt={banner.title}
                      onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="100"><rect fill="%232d2d2d" width="300" height="100"/><text fill="%239a9590" x="150" y="55" text-anchor="middle" font-size="14">Görsel Yüklenemedi</text></svg>'; }}
                    />
                    {banner.is_active === 0 && (
                      <div className="banner-inactive-overlay">
                        <span>Pasif</span>
                      </div>
                    )}
                  </div>
                  <div className="banner-info">
                    <div className="banner-info-top">
                      <h4 className="banner-title">{banner.title}</h4>
                      <span className={`banner-position-badge position-${banner.position}`}>
                        {POSITION_LABELS[banner.position] || banner.position}
                      </span>
                    </div>
                    {banner.link && (
                      <a href={banner.link} target="_blank" rel="noopener noreferrer" className="banner-link-preview">
                        {banner.link.length > 40 ? banner.link.substring(0, 40) + '...' : banner.link}
                      </a>
                    )}
                    <div className="banner-meta">
                      <span className="banner-meta-item">{banner.created_by_username || 'Bilinmiyor'}</span>
                      <span className="banner-meta-item">{formatDate(banner.created_at)}</span>
                    </div>
                  </div>
                  <div className="banner-actions">
                    <button
                      onClick={() => handleToggleBanner(banner.id)}
                      className={`banner-action-btn toggle ${banner.is_active === 1 ? 'on' : 'off'}`}
                      title={banner.is_active === 1 ? 'Pasif Yap' : 'Aktif Yap'}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {banner.is_active === 1 ? (
                          <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
                        ) : (
                          <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></>
                        )}
                      </svg>
                    </button>
                    <button
                      onClick={() => handleEditBanner(banner)}
                      className="banner-action-btn edit"
                      title="Düzenle"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteBanner(banner.id)}
                      className="banner-action-btn delete"
                      title="Sil"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ==================== MODALS ==================== */}
      <LiveStudioModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingStudio(null); }}
        onSubmit={handleModalSubmit}
        editingStudio={editingStudio}
        API_URL={API_URL}
      />

      <BannerModal
        isOpen={isBannerModalOpen}
        onClose={() => { setIsBannerModalOpen(false); setEditingBanner(null); }}
        onSubmit={handleBannerModalSubmit}
        editingBanner={editingBanner}
        API_URL={API_URL}
      />
    </div>
  );
};

export default LiveStudioTab;